import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import {
  collection,
  doc,
  getDocs,
  setDoc,
  onSnapshot,
  updateDoc,
  deleteDoc,
} from 'firebase/firestore';
import { deleteObject, ref } from 'firebase/storage';
import { db, storage } from '../lib/firebase';
import { useAuth } from './AuthContext';
import { PetProfile, PetSharedRef } from '../types';
import { useTrialStatus } from '../hooks/useTrialStatus';
import { useToast } from '../hooks/useToast';
import { clearReminderDataForPet } from '../lib/reminderEngine';

export interface RemovedPetNotice {
  pet_id: string;
  pet_name: string;
  owner_uid: string;
  removed_at: string;
}

const ACTIVE_PET_KEY = 'kodacare_active_pet';
const STARTER_PET_LIMIT = 1;
const VITAL_PET_LIMIT = 4;
const LIFETIME_PET_LIMIT = 30;

// ─── Context shape ────────────────────────────────────────────────────────────

interface PetContextType {
  pets: PetProfile[];
  activePet: PetProfile | null;
  activePetId: string | null;
  /** UID of the user who owns the active pet. Equals firebaseUser.uid for own pets; the original owner's uid for shared pets. */
  activePetOwnerUid: string | null;
  /** True when active pet is shared and current user is a subordinate/co-carer. */
  isActivePetSubordinate: boolean;
  petsLoading: boolean;
  petLimit: number;
  canCreatePet: boolean;
  setActivePet: (petId: string) => void;
  createPet: (data: Omit<PetProfile, 'id' | 'user_id' | 'created_at'>) => Promise<string>;
  updatePet: (petId: string, updates: Partial<Omit<PetProfile, 'id' | 'user_id'>>) => Promise<void>;
  deletePet: (petId: string) => Promise<void>;
  /** Leave a shared (co-carer) pet — removes the caller from the owner's members list
   *  and drops their own shared_pet_refs pointer. Does NOT delete the pet's data. */
  leaveSharedPet: (petId: string) => Promise<void>;
  /** Notices written by the server when a co-carer is removed by the owner. */
  removedPetNotices: RemovedPetNotice[];
  /** Acknowledge & delete a removed-pet notice. */
  dismissRemovedPetNotice: (petId: string) => Promise<void>;
}

const PetContext = createContext<PetContextType | undefined>(undefined);

// ─── Provider ─────────────────────────────────────────────────────────────────

export function PetProvider({ children }: { children: React.ReactNode }) {
  const { firebaseUser } = useAuth();
  const { hasActiveSubscription, subscription } = useTrialStatus();
  const { showToast, ToastMount } = useToast();
  const uid = firebaseUser?.uid ?? null;

  const [pets, setPets] = useState<PetProfile[]>([]);
  const [sharedRefs, setSharedRefs] = useState<PetSharedRef[]>([]);
  const [sharedPetFullProfiles, setSharedPetFullProfiles] = useState<Map<string, PetProfile>>(new Map());
  const [removedPetNotices, setRemovedPetNotices] = useState<RemovedPetNotice[]>([]);
  const [petsLoading, setPetsLoading] = useState(true);
  const [activePetId, setActivePetIdState] = useState<string | null>(
    () => localStorage.getItem(ACTIVE_PET_KEY)
  );

  const petLimit = useMemo(() => {
    if (!hasActiveSubscription) return STARTER_PET_LIMIT;
    const planId = (subscription?.planId ?? '').toLowerCase();
    const planName = (subscription?.planName ?? '').toLowerCase();
    if (planId === 'lifetime' || planName.includes('lifetime') || planName.includes('expert companion')) return LIFETIME_PET_LIMIT;
    return VITAL_PET_LIMIT;
  }, [hasActiveSubscription, subscription?.planId, subscription?.planName]);

  // ── Subscribe to pets collection ──────────────────────────────────────────
  useEffect(() => {
    if (!uid) {
      setPets([]);
      setSharedRefs([]);
      setRemovedPetNotices([]);
      setPetsLoading(false);
      return;
    }

    setPetsLoading(true);
    const unsubOwn = onSnapshot(
      collection(db, 'users', uid, 'pets'),
      snap => {
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() } as PetProfile));
        // Sort by creation time so order is stable
        list.sort((a, b) => a.created_at.localeCompare(b.created_at));
        setPets(list);
        setPetsLoading(false);
      },
      err => {
        console.error('[PetContext] Failed to subscribe to pets:', err);
        setPetsLoading(false);
      }
    );

    const unsubShared = onSnapshot(
      collection(db, 'users', uid, 'shared_pet_refs'),
      snap => {
        setSharedRefs(
          snap.docs.map((d) => {
            const data = d.data() as Record<string, unknown>;
            return {
              pet_id: d.id,
              owner_uid: (data.owner_uid as string) || (data.ownerUid as string) || '',
              pet_name: (data.pet_name as string) || (data.petName as string) || d.id,
              species: (data.species as PetSharedRef['species']) || 'dog',
              color: (data.color as string) || '#7c6f64',
              breed: (data.breed as string) || undefined,
              dob: (data.dob as string) || undefined,
              role: (data.role as PetSharedRef['role']) || 'carer',
              accepted_at: (data.accepted_at as string) || (data.joined_at as string) || new Date().toISOString(),
            } as PetSharedRef;
          })
        );
      },
      err => {
        console.warn('[PetContext] Failed to subscribe to shared_pet_refs:', err);
      }
    );

    const unsubRemoved = onSnapshot(
      collection(db, 'users', uid, 'removed_pet_notices'),
      snap => {
        setRemovedPetNotices(
          snap.docs.map(d => ({ pet_id: d.id, ...(d.data() as Omit<RemovedPetNotice, 'pet_id'>) }))
        );
      },
      err => {
        console.warn('[PetContext] Failed to subscribe to removed_pet_notices:', err);
      }
    );

    return () => { unsubOwn(); unsubShared(); unsubRemoved(); };
  }, [uid]);

  // ── Subscribe to the full pet doc for each shared pet ────────────────────
  // This gives the subordinate live access to all fields: breed, dob, vet info.
  useEffect(() => {
    if (sharedRefs.length === 0) {
      setSharedPetFullProfiles(new Map());
      return;
    }
    const profiles = new Map<string, PetProfile>();
    const unsubs = sharedRefs.map(ref =>
      onSnapshot(
        doc(db, 'users', ref.owner_uid, 'pets', ref.pet_id),
        snap => {
          if (snap.exists()) {
            profiles.set(ref.pet_id, {
              ...(snap.data() as Omit<PetProfile, 'id' | 'user_id'>),
              id: ref.pet_id,
              user_id: ref.owner_uid,
            });
          } else {
            profiles.delete(ref.pet_id);
          }
          setSharedPetFullProfiles(new Map(profiles));
        },
        err => { console.warn('[PetContext] shared pet doc subscription error:', err); }
      )
    );
    return () => unsubs.forEach(u => u());
  }, [sharedRefs]);

  // ── Ensure activePetId always points to a real pet ───────────────────────
  useEffect(() => {
    const combined = [
      ...pets,
      ...sharedRefs.map(r => ({ id: r.pet_id } as PetProfile)),
    ];
    if (petsLoading) return;
    if (combined.length === 0) {
      if (activePetId) {
        setActivePetIdState(null);
        localStorage.removeItem(ACTIVE_PET_KEY);
      }
      return;
    }
    if (!activePetId || !combined.find(p => p.id === activePetId)) {
      const firstId = combined[0].id;
      setActivePetIdState(firstId);
      localStorage.setItem(ACTIVE_PET_KEY, firstId);
    }
  }, [pets, sharedRefs, activePetId, petsLoading]);

  // Build a combined list: own pets first, then shared pets as PetProfile shapes
  const allPets = useMemo(() => {
    const ownPetIds = new Set(pets.map((pet) => pet.id));
    const sharedAsPetProfiles: PetProfile[] = sharedRefs
      .filter((ref) => !ownPetIds.has(ref.pet_id))
      .map(ref => {
        // Prefer the live full pet doc; fall back to the minimal shared ref snapshot
        const full = sharedPetFullProfiles.get(ref.pet_id);
        if (full) return full;
        return {
          id: ref.pet_id,
          user_id: ref.owner_uid,
          name: ref.pet_name,
          species: ref.species,
          color: ref.color,
          breed: ref.breed,
          dob: ref.dob,
          created_at: ref.accepted_at,
        };
      });

    return [...pets, ...sharedAsPetProfiles];
  }, [pets, sharedRefs, sharedPetFullProfiles]);

  const activePet = useMemo(
    () => allPets.find(p => p.id === activePetId) ?? null,
    [allPets, activePetId]
  );

  // For shared pets, the owner uid comes from the sharedRef; for own pets it equals uid
  const activePetOwnerUid = useMemo(() => {
    if (!activePetId) return null;
    const sharedRef = sharedRefs.find(r => r.pet_id === activePetId);
    return sharedRef ? sharedRef.owner_uid : uid;
  }, [activePetId, sharedRefs, uid]);

  const isActivePetSubordinate = useMemo(() => {
    if (!uid || !activePetOwnerUid) return false;
    return uid !== activePetOwnerUid;
  }, [uid, activePetOwnerUid]);

  // ── Actions ───────────────────────────────────────────────────────────────

  const setActivePet = useCallback((petId: string) => {
    setActivePetIdState(petId);
    localStorage.setItem(ACTIVE_PET_KEY, petId);
  }, []);

  const createPet = useCallback(
    async (data: Omit<PetProfile, 'id' | 'user_id' | 'created_at'>): Promise<string> => {
      if (!uid) throw new Error('Not authenticated');
      if (pets.length >= petLimit) throw new Error('PET_LIMIT_REACHED');

      // Hard gate: co-carers (users with shared pets but no owned pets) cannot add pets
      // without an active subscription
      const ownedPetCount = pets.filter(p => p.user_id === uid).length;
      const hasSharedPets = sharedRefs.length > 0;
      const isCoCarer = ownedPetCount === 0 && hasSharedPets;
      
      if (isCoCarer && !hasActiveSubscription) {
        throw new Error('CO_CARER_SUBSCRIPTION_REQUIRED');
      }

      const newRef = doc(collection(db, 'users', uid, 'pets'));
      const createdAt = new Date().toISOString();
      const optimistic: PetProfile = { id: newRef.id, user_id: uid, created_at: createdAt, ...data };

      // Instant local update — panel closes and the pet card appears immediately.
      setPets(prev =>
        [...prev, optimistic].sort((a, b) => a.created_at.localeCompare(b.created_at))
      );

      setDoc(newRef, { ...data, user_id: uid, created_at: createdAt }).catch(err => {
        console.error('[PetContext] createPet failed:', err);
        setPets(prev => prev.filter(p => p.id !== newRef.id));
        showToast('Could not save pet. Please try again.', 'error');
      });

      return newRef.id;
    },
    [uid, pets, petLimit, sharedRefs, hasActiveSubscription, showToast]
  );

  const updatePet = useCallback(
    async (petId: string, updates: Partial<Omit<PetProfile, 'id' | 'user_id'>>) => {
      if (!uid) return;

      let previous: PetProfile | undefined;
      setPets(prev => {
        previous = prev.find(p => p.id === petId);
        return prev.map(p => p.id === petId ? { ...p, ...updates } : p);
      });

      updateDoc(doc(db, 'users', uid, 'pets', petId), updates as Record<string, unknown>)
        .catch(err => {
          console.error('[PetContext] updatePet failed:', err);
          if (previous) setPets(prev => prev.map(p => p.id === petId ? previous! : p));
          showToast('Could not update pet profile. Please try again.', 'error');
        });
    },
    [uid, showToast]
  );

  const deletePet = useCallback(
    async (petId: string) => {
      if (!uid) return;

      const petBase = `users/${uid}/pets/${petId}`;

      // Delete vault documents (storage object + metadata doc)
      const documentsSnap = await getDocs(collection(db, petBase, 'documents'));
      await Promise.all(
        documentsSnap.docs.map(async d => {
          const storagePath = d.data()?.storage_path;
          if (typeof storagePath === 'string' && storagePath.length > 0) {
            try {
              await deleteObject(ref(storage, storagePath));
            } catch (err) {
              console.warn('[PetContext] Failed to delete pet storage object:', err);
            }
          }
          await deleteDoc(d.ref);
        })
      );

      // Delete medications
      const medicationsSnap = await getDocs(collection(db, petBase, 'medications'));
      await Promise.all(medicationsSnap.docs.map(d => deleteDoc(d.ref)));

      // Delete contacts
      const contactsSnap = await getDocs(collection(db, petBase, 'contacts'));
      await Promise.all(contactsSnap.docs.map(d => deleteDoc(d.ref)));

      // Delete logs and nested entries
      const logDatesSnap = await getDocs(collection(db, petBase, 'logs'));
      for (const dateDoc of logDatesSnap.docs) {
        const entriesSnap = await getDocs(collection(db, petBase, 'logs', dateDoc.id, 'entries'));
        await Promise.all(entriesSnap.docs.map(entryDoc => deleteDoc(entryDoc.ref)));
        await deleteDoc(dateDoc.ref);
      }

      // Delete fellowship XP events
      const xpEventsSnap = await getDocs(collection(db, petBase, 'fellowship_xp_events'));
      await Promise.all(xpEventsSnap.docs.map(d => deleteDoc(d.ref)));

      // Delete fellowship profile
      await deleteDoc(doc(db, petBase, 'fellowship', 'profile'));

      // Remove public leaderboard entry
      await deleteDoc(doc(db, 'public_companions', `${uid}_${petId}`));

      localStorage.removeItem(`koda_medical_profile_${petId}`);
      clearReminderDataForPet(petId);

      await deleteDoc(doc(db, 'users', uid, 'pets', petId));
      if (activePetId === petId) {
        const remaining = pets.filter(p => p.id !== petId);
        if (remaining.length > 0) {
          setActivePet(remaining[0].id);
        } else {
          setActivePetIdState(null);
          localStorage.removeItem(ACTIVE_PET_KEY);
        }
      }
    },
    [uid, activePetId, pets, setActivePet]
  );

  const leaveSharedPet = useCallback(
    async (petId: string) => {
      if (!uid) return;
      const sharedRef = sharedRefs.find(r => r.pet_id === petId);
      if (!sharedRef) return;

      // Remove the member doc on the owner's side.
      // Firestore rules allow a member to delete their own membership doc.
      await deleteDoc(doc(db, 'users', sharedRef.owner_uid, 'pets', petId, 'members', uid));
      // Remove the shared_pet_refs pointer from our own account.
      await deleteDoc(doc(db, 'users', uid, 'shared_pet_refs', petId));
      clearReminderDataForPet(petId);

      // Switch away from this pet if it was active.
      if (activePetId === petId) {
        const remaining = allPets.filter(p => p.id !== petId);
        if (remaining.length > 0) {
          setActivePet(remaining[0].id);
        } else {
          setActivePetIdState(null);
          localStorage.removeItem(ACTIVE_PET_KEY);
        }
      }
    },
    [uid, sharedRefs, activePetId, allPets, setActivePet]
  );

  const canCreatePet = pets.length < petLimit; // only own pets count toward the limit

  const dismissRemovedPetNotice = useCallback(async (petId: string) => {
    if (!uid) return;
    try {
      await deleteDoc(doc(db, 'users', uid, 'removed_pet_notices', petId));
    } catch (err) {
      console.warn('[PetContext] Failed to dismiss removed pet notice:', err);
    }
  }, [uid]);

  const contextValue = useMemo(
    () => ({
      pets: allPets,
      activePet,
      activePetId,
      activePetOwnerUid,
      isActivePetSubordinate,
      petsLoading,
      petLimit,
      canCreatePet,
      setActivePet,
      createPet,
      updatePet,
      deletePet,
      leaveSharedPet,
      removedPetNotices,
      dismissRemovedPetNotice,
    }),
    [allPets, activePet, activePetId, activePetOwnerUid, isActivePetSubordinate, petsLoading, petLimit, canCreatePet, setActivePet, createPet, updatePet, deletePet, leaveSharedPet, removedPetNotices, dismissRemovedPetNotice]
  );

  return (
    <PetContext.Provider value={contextValue}>
      {children}
      <ToastMount />
    </PetContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function usePet() {
  const context = useContext(PetContext);
  if (!context) throw new Error('usePet must be used within a PetProvider');
  return context;
}
